import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

export type ProjectsShadowStageProps = {
  /** `embed` = home / inline; `standalone` = full `/3D` page (taller viewport). */
  variant?: "embed" | "standalone";
};

/** Shadow-map showcase: floor, torus knot, cube, directional + spot lights. */
export default function ProjectsShadowStage({
  variant = "embed",
}: ProjectsShadowStageProps) {
  const mountRef = useRef<HTMLDivElement>(null);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduceMotion(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    if (reduceMotion) return;

    const mount = mountRef.current;
    if (!mount) return;

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: false,
      powerPreference: "high-performance",
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);

    const camera = new THREE.PerspectiveCamera(42, 1, 0.5, 120);
    camera.position.set(8.2, 6.8, 9.5);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.06;
    controls.target.set(0, 0.85, 0);
    controls.minDistance = 7;
    controls.maxDistance = 24;
    controls.maxPolarAngle = Math.PI / 2 - 0.06;

    const planeGeo = new THREE.PlaneGeometry(26, 26);
    const planeMat = new THREE.MeshLambertMaterial({ color: 0xbdbdbd });
    const plane = new THREE.Mesh(planeGeo, planeMat);
    plane.rotation.x = -Math.PI / 2;
    plane.receiveShadow = true;
    scene.add(plane);

    const meshMat = new THREE.MeshStandardMaterial({
      color: 0xb71c1c,
      metalness: 0.15,
      roughness: 0.42,
    });
    const knotGeo = new THREE.TorusKnotGeometry(1, 0.32, 128, 24);
    const knot = new THREE.Mesh(knotGeo, meshMat);
    knot.position.set(0, 1.38, 0);
    knot.castShadow = true;
    scene.add(knot);

    const cubeMat = meshMat.clone();
    const cubeGeo = new THREE.BoxGeometry(1.28, 1.28, 1.28);
    const cube = new THREE.Mesh(cubeGeo, cubeMat);
    cube.position.set(-2.85, 0.66, 1.05);
    cube.castShadow = true;
    scene.add(cube);

    scene.add(new THREE.AmbientLight(0xffffff, 0.26));

    const dirLight = new THREE.DirectionalLight(0xffffff, 0.92);
    dirLight.position.set(6, 14, 11);
    dirLight.castShadow = true;
    dirLight.shadow.mapSize.set(2048, 2048);
    dirLight.shadow.bias = -0.00012;
    const oc = dirLight.shadow.camera;
    oc.near = 1;
    oc.far = 38;
    oc.left = -13;
    oc.right = 13;
    oc.top = 13;
    oc.bottom = -13;
    scene.add(dirLight);

    const spotLight = new THREE.SpotLight(0xffffff, 1.12);
    spotLight.position.set(-8.5, 12.5, 5.5);
    spotLight.angle = Math.PI / 5;
    spotLight.penumbra = 0.38;
    spotLight.decay = 2;
    spotLight.distance = 48;
    spotLight.castShadow = true;
    spotLight.shadow.mapSize.set(1024, 1024);
    spotLight.shadow.bias = -0.00008;
    spotLight.target.position.set(0, 0.4, 0);
    scene.add(spotLight);
    scene.add(spotLight.target);

    const dirHelper = new THREE.DirectionalLightHelper(dirLight, 2.8, 0xff6600);
    scene.add(dirHelper);
    const spotHelper = new THREE.SpotLightHelper(spotLight);
    scene.add(spotHelper);

    const setSize = () => {
      const w = mount.clientWidth;
      const h = Math.max(mount.clientHeight, 180);
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    setSize();

    renderer.domElement.style.display = "block";
    renderer.domElement.style.width = "100%";
    renderer.domElement.style.height = "100%";
    renderer.domElement.style.borderRadius = "10px";
    mount.appendChild(renderer.domElement);

    const clock = new THREE.Clock();
    let raf = 0;

    const tick = () => {
      raf = requestAnimationFrame(tick);
      const t = clock.getElapsedTime();
      knot.rotation.x = t * 0.38;
      knot.rotation.y = t * 0.52;
      controls.update();
      spotHelper.update();
      dirHelper.update();
      renderer.render(scene, camera);
    };
    tick();

    const ro = new ResizeObserver(setSize);
    ro.observe(mount);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      controls.dispose();
      spotHelper.dispose();
      dirHelper.dispose();
      planeGeo.dispose();
      planeMat.dispose();
      knotGeo.dispose();
      meshMat.dispose();
      cubeGeo.dispose();
      cubeMat.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, [reduceMotion]);

  if (reduceMotion) {
    return (
      <div className="projects-shadow-stage projects-shadow-stage--fallback rounded">
        <span className="small text-secondary">
          Shadow-map showcase uses motion; enable animations in system settings to
          interact.
        </span>
      </div>
    );
  }

  const size =
    variant === "standalone"
      ? { height: "min(52vh, 560px)", minHeight: 220 }
      : { height: "min(32vh, 320px)", minHeight: 200 };

  return (
    <div
      ref={mountRef}
      className="projects-shadow-stage rounded"
      style={{
        width: "100%",
        touchAction: "none",
        ...size,
      }}
      aria-label="Interactive shadow map scene — drag to orbit"
    />
  );
}
